type MiddlewareFunction<T> = ((ctx: T, next: (ctx?: T) => Promise<void>) => Promise<void>) | ((ctx: T) => Promise<void>);

// https://github.com/koajs/compose/blob/master/index.js
/**
 * @typeParam T - Type of context passed through the middleware
 * @param middleware middleware stack
 * @return {MiddlewareFunction}
 */
export function compose<T>(...middleware: MiddlewareFunction<T>[]): (ctx: T) => Promise<void> {
    if (!Array.isArray(middleware)) {
        throw new Error('middleware must be array!');
    }
    const fns = middleware.flat();
    for (const fn of fns) {
        if (typeof fn !== 'function') {
            throw new Error('middleware must be compose of functions!');
        }
    }
    return async function (ctx: T, next?: (ctx?: T) => Promise<void>) {
        const dispatch = async (i: number, dispatchedCtx: T): Promise<void> => {
            const fn = i === fns.length ? next : fns[i];
            if (fn === undefined) {
                return;
            }
            let nextCalled = false;
            let nextResolved = false;
            const nextFn = async (nextCtx?: T) => {
                if (nextCalled) {
                    throw new Error('next() called multiple times');
                }
                nextCalled = true;
                try {
                    return await dispatch(i + 1, nextCtx ?? dispatchedCtx);
                }
                finally {
                    nextResolved = true;
                }
            };
            const result = await fn(dispatchedCtx, nextFn);
            if (nextCalled && !nextResolved) {
                throw new Error('middleware resolved before downstream.\n\t You are probably missing an await or return statement in your middleware function.');
            }
            return result;
        }

        return dispatch(0, ctx);
    }
}
